import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "../../lib/lang";
import { WORKS, SERVICE_COLORS } from "../../lib/content";
import { SectionHead, EASE } from "./Reveal";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const WorkTile = ({ w, lang, bento, onOpen, i }) => (
  <motion.button
    layout
    data-testid={`portfolio-item-${w.id}`}
    onClick={() => onOpen(w)}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: EASE }}
    className={`group relative overflow-hidden rounded-[22px] border border-white/10 bg-[#13182E] text-left ${bento ? w.span : ""}`}
  >
    <img src={w.img} alt={w.title[lang]} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07]" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E1B] via-[#0B0E1B]/10 to-transparent opacity-90" />
    <span className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-md text-white transition-transform duration-500 group-hover:rotate-90 group-hover:bg-white group-hover:text-[#0B0E1B]">
      <Plus className="w-5 h-5" />
    </span>
    <div className="absolute bottom-0 inset-x-0 p-5 lg:p-6">
      <span className="inline-block mb-2 rounded-full px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-widest text-white" style={{ background: `${SERVICE_COLORS[w.cat] || "#38BDF8"}cc` }}>
        {w.cat}
      </span>
      <h3 className="font-display text-xl lg:text-2xl text-white leading-tight">{w.title[lang]}</h3>
      <p className="font-mono text-xs text-slate-300 mt-1 max-h-0 opacity-0 group-hover:max-h-6 group-hover:opacity-100 transition-[max-height,opacity] duration-500">{w.spec}</p>
    </div>
  </motion.button>
);

export const Portfolio = () => {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const bento = filter === "all";
  const list = bento ? WORKS : WORKS.filter((w) => w.cat === filter);

  return (
    <section id="work" className="relative mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <SectionHead eyebrow={t.workEyebrow} title={t.workTitle} testid="portfolio-title" />
        <div className="flex flex-wrap gap-2 mb-12 lg:mb-16" data-testid="portfolio-filters">
          {Object.entries(t.filters).map(([k, label]) => (
            <button key={k} data-testid={`portfolio-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-300 ${filter === k ? "bg-white text-[#0B0E1B] border-white" : "border-white/15 text-slate-300 hover:text-white hover:border-white/40"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <motion.div layout className={`grid gap-4 lg:gap-5 auto-rows-[300px] md:auto-rows-[260px] lg:auto-rows-[240px] ${bento ? "md:grid-cols-2 lg:grid-cols-12" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {list.map((w, i) => <WorkTile key={w.id} w={w} i={i} lang={lang} bento={bento} onOpen={setActive} />)}
      </motion.div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="portfolio-lightbox" className="max-w-3xl bg-[#0B0E1B] border-white/10 p-3 text-white">
          {active && (
            <>
              <img src={active.img} alt={active.title[lang]} className="w-full max-h-[70vh] object-contain rounded-xl" />
              <div className="px-2 pb-2 flex flex-wrap items-baseline justify-between gap-2">
                <DialogTitle className="font-display text-2xl">{active.title[lang]}</DialogTitle>
                <span className="font-mono text-xs text-sky-300">{active.spec}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
